import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Box, CircularProgress, Alert, IconButton,
  Typography, Switch, FormControlLabel
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import Swal from 'sweetalert2'
import { uploadImageToGhl, useAppServices } from 'hook/services'

const initialForm = {
  title: '',
  shortAddress: '',
  url: '',
  desktopImage: '',
  mobileImage: '',
  order: 0,
  isActive: true,
}

export default function CreateBannerModal({ handleRefresh }) {
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState(initialForm)
  const [uploadingDesktop, setUploadingDesktop] = useState(false)
  const [uploadingMobile, setUploadingMobile] = useState(false)
  const AppService = useAppServices()

  const handleOpen = () => {
    setForm(initialForm)
    setError('')
    setOpen(true)
  }

  const handleClose = () => setOpen(false)

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }))

  const handleSwitch = (e) =>
    setForm((prev) => ({ ...prev, isActive: e.target.checked }))

  const handleImageUpload = async (e, field, setUploading) => {
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    setUploading(true)
    try {
      const response = await uploadImageToGhl(formData)
      setForm((prev) => ({ ...prev, [field]: response.imageUrl || response.data }))
    } catch (err) {
      Swal.fire('Upload Failed', 'Image upload failed. Try again.', 'error')
    } finally {
      setUploading(false)
    }
  }

  const handleSave = async () => {
    if (!form.desktopImage || !form.mobileImage) {
      setError('Both desktop and mobile images are required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = { ...form, order: Number(form.order) }
      await AppService.banner.create({ payload })
      setOpen(false)
      handleRefresh()
      Swal.fire({
        icon: 'success',
        title: 'Banner Created!',
        timer: 2000,
        showConfirmButton: false,
      })
    } catch (err) {
      setError('Failed to create banner. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          backgroundColor: '#f0f4ff',
          color: '#1A73E8',
          fontWeight: 700,
          fontSize: '0.8rem',
          borderRadius: '8px',
          textTransform: 'none',
          px: 2,
          '&:hover': { backgroundColor: '#dce8ff' },
        }}
      >
        Add Banner
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #1A73E8 0%, #0D47A1 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 2, px: 3,
          }}
        >
          <Typography variant="h6" fontWeight={700} style={{ color: 'white' }} fontSize="1rem">
            Add Homepage Banner
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 4, py: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>
          )}

          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title (optional)"
              fullWidth
              value={form.title}
              onChange={handleChange('title')}
              placeholder="Location Title"
            />

            <TextField
              label="Short Address (optional)"
              fullWidth
              value={form.shortAddress}
              onChange={handleChange('shortAddress')}
              placeholder="e.g. Bellevue Hill, Sydney"
              helperText="Banner par text overlay me dikhega"
            />

            <TextField
              label="Link URL (optional)"
              fullWidth
              value={form.url}
              onChange={handleChange('url')}
              placeholder="https://aloca.com.au/listing/..."
              helperText="Banner click par yahan jayega"
            />

            <TextField
              label="Display Order"
              type="number"
              fullWidth
              value={form.order}
              onChange={handleChange('order')}
              inputProps={{ min: 0 }}
              helperText="Lower numbers appear first in the carousel"
            />

            {/* Desktop Image */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1 }}>
                DESKTOP IMAGE <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                Recommended: 1920×700px landscape
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploadingDesktop}
                  sx={{ textTransform: 'none', color: '#1A73E8', minWidth: 130 }}
                >
                  {uploadingDesktop ? <CircularProgress size={16} /> : 'Upload Desktop'}
                  <input
                    hidden type="file" accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'desktopImage', setUploadingDesktop)}
                  />
                </Button>
                {form.desktopImage && (
                  <Box
                    component="img"
                    src={`${form.desktopImage}?width=160&quality=70`}
                    alt="Desktop preview"
                    sx={{ width: 120, height: 60, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                )}
              </Box>
            </Box>

             <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 1 }}>
                MOBILE IMAGE <span style={{ color: '#d32f2f' }}>*</span>
              </Typography>
              <Typography variant="caption" display="block" color="text.secondary" mb={1}>
                Recommended: 768×950px portrait — subject centered to avoid squishing
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploadingMobile}
                  sx={{ textTransform: 'none', color: '#1A73E8', minWidth: 130 }}
                >
                  {uploadingMobile ? <CircularProgress size={16} /> : 'Upload Mobile'}
                  <input
                    hidden type="file" accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'mobileImage', setUploadingMobile)}
                  />
                </Button>
                {form.mobileImage && (
                  <Box
                    component="img"
                    src={`${form.mobileImage}?width=80&quality=70`}
                    alt="Mobile preview"
                    sx={{ width: 60, height: 72, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                )}
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Switch checked={form.isActive} onChange={handleSwitch} color="success" />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Active (show in carousel)
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, gap: 1 }}>
          <Button onClick={handleClose} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || uploadingDesktop || uploadingMobile}
            sx={{
              background: 'linear-gradient(135deg, #1A73E8, #0D47A1)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
            }}
            style={{ color: 'white' }}
          >
            {saving ? <CircularProgress size={16} color="inherit" /> : 'Save Banner'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}